<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useCalendarSourcesStore } from 'dashboard/stores/calendarSources';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const emit = defineEmits(['sourcesUpdated']);

const { t } = useI18n();
const calendarSourcesStore = useCalendarSourcesStore();

const dialogRef = ref(null);
const form = reactive({ name: '', url: '', color: '#1F93FF' });
const isCreating = ref(false);

const sources = computed(() => calendarSourcesStore.getRecords);
const isFormInvalid = computed(() => !form.name.trim() || !form.url.trim());

const resetForm = () => {
  form.name = '';
  form.url = '';
  form.color = '#1F93FF';
};

const open = () => {
  calendarSourcesStore.get();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const syncStatusLabel = source => {
  if (source.lastSyncError) {
    return t('CALENDAR.SOURCES.STATUS.ERROR', { error: source.lastSyncError });
  }
  if (!source.lastSyncedAt) {
    return t('CALENDAR.SOURCES.STATUS.NEVER_SYNCED');
  }
  return t('CALENDAR.SOURCES.STATUS.SYNCED', {
    time: formatDistanceToNow(new Date(source.lastSyncedAt), {
      addSuffix: true,
    }),
  });
};

const handleCreate = async () => {
  if (isFormInvalid.value) return;
  isCreating.value = true;
  try {
    await calendarSourcesStore.create({
      name: form.name.trim(),
      url: form.url.trim(),
      color: form.color,
    });
    resetForm();
    useAlert(t('CALENDAR.SOURCES.MESSAGES.CREATE_SUCCESS'));
    emit('sourcesUpdated');
  } catch {
    useAlert(t('CALENDAR.SOURCES.MESSAGES.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const toggleEnabled = async source => {
  try {
    await calendarSourcesStore.update({
      id: source.id,
      enabled: !source.enabled,
    });
    emit('sourcesUpdated');
  } catch {
    useAlert(t('CALENDAR.SOURCES.MESSAGES.UPDATE_ERROR'));
  }
};

const deleteSource = async source => {
  try {
    await calendarSourcesStore.delete(source.id);
    useAlert(t('CALENDAR.SOURCES.MESSAGES.DELETE_SUCCESS'));
    emit('sourcesUpdated');
  } catch {
    useAlert(t('CALENDAR.SOURCES.MESSAGES.DELETE_ERROR'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="t('CALENDAR.SOURCES.TITLE')"
    :description="t('CALENDAR.SOURCES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-3">
        <span v-if="!sources.length" class="text-sm text-n-slate-11">
          {{ t('CALENDAR.SOURCES.EMPTY_STATE') }}
        </span>
        <div
          v-for="source in sources"
          :key="source.id"
          class="flex items-center justify-between gap-3 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex items-center min-w-0 gap-2">
            <span
              class="rounded-full size-2.5 shrink-0"
              :style="{ backgroundColor: source.color }"
            />
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ source.name }}
              </span>
              <span
                class="text-xs truncate"
                :class="
                  source.lastSyncError ? 'text-n-ruby-9' : 'text-n-slate-11'
                "
              >
                {{ syncStatusLabel(source) }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <Switch
              :model-value="source.enabled"
              @change="toggleEnabled(source)"
            />
            <Button
              icon="i-lucide-trash-2"
              color="ruby"
              variant="ghost"
              size="sm"
              @click="deleteSource(source)"
            />
          </div>
        </div>
      </div>

      <form class="flex flex-col gap-4" @submit.prevent="handleCreate">
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.name"
            :label="t('CALENDAR.SOURCES.FORM.NAME.LABEL')"
            :placeholder="t('CALENDAR.SOURCES.FORM.NAME.PLACEHOLDER')"
            :disabled="isCreating"
          />
          <Input
            v-model="form.url"
            :label="t('CALENDAR.SOURCES.FORM.URL.LABEL')"
            :placeholder="t('CALENDAR.SOURCES.FORM.URL.PLACEHOLDER')"
            :disabled="isCreating"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.SOURCES.FORM.COLOR.LABEL') }}
          </span>
          <ColorPicker v-model="form.color" />
        </div>
        <Button
          :label="t('CALENDAR.SOURCES.FORM.SAVE')"
          color="blue"
          type="submit"
          class="self-end"
          :disabled="isFormInvalid || isCreating"
          :is-loading="isCreating"
        />
      </form>
    </div>
  </Dialog>
</template>
